(() => {
  const labelsElement = document.querySelector('[data-kiddyview-labels]');
  let labels = {};
  if (labelsElement) {
    try { labels = JSON.parse(labelsElement.textContent); } catch (error) { labels = {}; }
  }

  const compatibilityStorageKey = 'kiddyview:compatibility:v1';
  const readCompatibilityResult = () => {
    for (const storageName of ['localStorage', 'sessionStorage']) {
      try {
        const result = JSON.parse(window[storageName].getItem(compatibilityStorageKey));
        return result?.version === 1 && result.wired === true && result.originalTouchscreen === true
          ? result : null;
      } catch (error) {
        // Try session storage when persistent storage is unavailable.
      }
    }
    return null;
  };
  let compatibilityResult = readCompatibilityResult();
  const notifyCompatibilityChange = () => document.dispatchEvent(new CustomEvent('kiddyview:compatibility-change'));
  const setCompatibilityResult = (passed) => {
    compatibilityResult = passed ? { version: 1, wired: true, originalTouchscreen: true } : null;
    for (const storageName of ['localStorage', 'sessionStorage']) {
      try {
        if (compatibilityResult) window[storageName].setItem(compatibilityStorageKey, JSON.stringify(compatibilityResult));
        else window[storageName].removeItem(compatibilityStorageKey);
      } catch (error) {
        // The current page still works when browser storage is blocked.
      }
    }
    notifyCompatibilityChange();
  };
  document.addEventListener('kiddyview:compatibility-result', (event) => {
    setCompatibilityResult(event.detail?.passed === true);
  });
  window.addEventListener('storage', (event) => {
    if (event.key !== compatibilityStorageKey && event.key !== null) return;
    compatibilityResult = readCompatibilityResult();
    notifyCompatibilityChange();
  });
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    compatibilityResult = readCompatibilityResult();
    notifyCompatibilityChange();
  });

  const formatLabel = (template, values) => Object.entries(values).reduce(
    (result, [key, value]) => result.replace(`__${key.toUpperCase()}__`, String(value)),
    String(template || '')
  );

  function openLinkedFaq() {
    if (!/^#faq-question-\d+$/.test(window.location.hash)) return;
    const button = document.getElementById(window.location.hash.slice(1));
    if (!button?.matches('.faq-item__question')) return;
    const item = button.closest('.faq-item');
    const answer = document.getElementById(button.getAttribute('aria-controls'));
    if (!item || !answer) return;
    button.setAttribute('aria-expanded', 'true');
    item.classList.add('faq-item--open');
    answer.hidden = false;
    button.focus({ preventScroll: true });
    item.scrollIntoView({ block: 'start' });
  }

  function initializeFaq(root = document) {
    root.querySelectorAll('.faq-item__question').forEach((button) => {
      if (button.dataset.initialized === 'true') return;
      button.dataset.initialized = 'true';
      button.addEventListener('click', () => {
        const item = button.closest('.faq-item');
        const answer = document.getElementById(button.getAttribute('aria-controls'));
        const isOpen = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', String(!isOpen));
        item?.classList.toggle('faq-item--open', !isOpen);
        if (answer) answer.hidden = isOpen;
      });
    });
    openLinkedFaq();
  }

  function initializeProcess(root = document) {
    const process = root.querySelector('.process');
    if (!process || process.dataset.initialized === 'true') return;
    process.dataset.initialized = 'true';
    const steps = [...process.querySelectorAll('[data-process-step]')];
    const links = [...process.querySelectorAll('[data-process-link]')];
    const label = process.querySelector('[data-process-label]');
    const percentLabel = process.querySelector('[data-process-percent]');
    const bar = process.querySelector('[data-process-bar]');
    if (!steps.length) return;

    const setActive = (index) => {
      const percent = Math.round(((index + 1) / steps.length) * 100);
      steps.forEach((step, stepIndex) => step.classList.toggle('setup-timeline__item--active', stepIndex === index));
      links.forEach((link, linkIndex) => link.classList.toggle('process__nav-link--active', linkIndex === index));
      if (label && labels.processPart) label.textContent = formatLabel(labels.processPart, { current: index + 1, total: steps.length });
      if (percentLabel) percentLabel.textContent = `${percent}%`;
      if (bar) bar.style.width = `${percent}%`;
    };

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(Number(entry.target.getAttribute('data-process-step')));
        });
      }, { rootMargin: '-35% 0px -45% 0px', threshold: 0.08 });
      steps.forEach((step) => observer.observe(step));
    }
    setActive(0);
  }

  function initializeProductGallery(root = document) {
    root.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
      if (gallery.dataset.initialized === 'true') return;
      const stage = gallery.querySelector('[data-product-gallery-stage]');
      const slides = [...gallery.querySelectorAll('[data-product-gallery-slide]')];
      if (!stage || !slides.length) return;
      gallery.dataset.initialized = 'true';
      const thumbs = [...gallery.querySelectorAll('[data-product-thumb]')];
      const previous = gallery.querySelector('[data-product-gallery-prev]');
      const next = gallery.querySelector('[data-product-gallery-next]');
      const thumbRow = gallery.querySelector('.product-detail__thumb-row');
      let currentIndex = 0;
      let touchStart = null;

      // Keep translated text inside its original box at every image/thumbnail size.
      const fitArtwork = (artwork) => {
        if (!artwork.getBoundingClientRect().width) return;
        artwork.querySelectorAll('[data-gallery-fit]').forEach((box) => {
          const text = box.firstElementChild;
          if (!text) return;
          box.style.removeProperty('font-size');
          const bounds = box.getBoundingClientRect();
          const size = parseFloat(getComputedStyle(box).fontSize);
          if (!bounds.width || !bounds.height || !size) return;
          // Fractional bounds matter in thumbnails; integer scrollWidth rounds too much.
          for (let attempt = 0; attempt < 3; attempt += 1) {
            const content = text.getBoundingClientRect();
            const ratio = Math.min(1, bounds.width / Math.max(content.width, .01), bounds.height / Math.max(content.height, .01));
            if (ratio >= .998) break;
            box.style.fontSize = `${parseFloat(getComputedStyle(box).fontSize) * ratio * .99}px`;
          }
        });
      };
      const artworks = [...gallery.querySelectorAll('[data-gallery-artwork]')];
      const fitAll = () => artworks.forEach(fitArtwork);
      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver((entries) => entries.forEach((entry) => fitArtwork(entry.target)));
        artworks.forEach((artwork) => observer.observe(artwork));
        document.addEventListener('shopify:section:unload', (event) => {
          if (event.target.contains(gallery)) observer.disconnect();
        });
      } else {
        window.addEventListener('resize', fitAll, { passive: true });
      }
      document.fonts?.ready.then(fitAll);


      const setImage = (index, scrollThumb = true) => {
        currentIndex = (index + slides.length) % slides.length;
        slides.forEach((slide, slideIndex) => {
          slide.hidden = slideIndex !== currentIndex;
        });
        slides[currentIndex].querySelectorAll('[data-gallery-artwork]').forEach(fitArtwork);
        // Each slide keeps Shopify's own srcset and dimensions.
        const activeImage = slides[currentIndex].querySelector('img');
        if (activeImage) activeImage.loading = 'eager';
        thumbs.forEach((thumb, thumbIndex) => {
          const active = thumbIndex === currentIndex;
          thumb.classList.toggle('product-detail__thumb--active', active);
          thumb.setAttribute('aria-current', String(active));
        });
        const thumb = thumbs[currentIndex];
        if (scrollThumb && thumb && thumbRow && thumbRow.scrollWidth > thumbRow.clientWidth) {
          const rowBounds = thumbRow.getBoundingClientRect();
          const thumbBounds = thumb.getBoundingClientRect();
          thumbRow.scrollTo({
            left: thumbRow.scrollLeft + thumbBounds.left - rowBounds.left - (thumbRow.clientWidth - thumbBounds.width) / 2,
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
          });
        }
      };

      thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => setImage(index)));
      previous?.addEventListener('click', () => setImage(currentIndex - 1));
      next?.addEventListener('click', () => setImage(currentIndex + 1));
      gallery.addEventListener('keydown', (event) => {
        let index;
        if (event.key === 'ArrowLeft') index = currentIndex - 1;
        else if (event.key === 'ArrowRight') index = currentIndex + 1;
        else if (event.key === 'Home') index = 0;
        else if (event.key === 'End') index = slides.length - 1;
        else return;
        event.preventDefault();
        setImage(index);
        if (event.target.closest('[data-product-thumb]')) thumbs[currentIndex]?.focus({ preventScroll: true });
      });
      // Only the large image handles swipes; the thumbnail strip scrolls independently.
      stage.addEventListener('touchstart', (event) => {
        touchStart = event.touches.length === 1
          ? { x: event.touches[0].clientX, y: event.touches[0].clientY }
          : null;
      }, { passive: true });
      stage.addEventListener('touchcancel', () => { touchStart = null; }, { passive: true });
      stage.addEventListener('touchend', (event) => {
        const start = touchStart;
        touchStart = null;
        if (!start || event.touches.length || !event.changedTouches.length) return;
        const deltaX = event.changedTouches[0].clientX - start.x;
        const deltaY = event.changedTouches[0].clientY - start.y;
        if (Math.abs(deltaX) > 50 && Math.abs(deltaX) > Math.abs(deltaY)) {
          setImage(currentIndex + (deltaX < 0 ? 1 : -1));
        }
      }, { passive: true });
      setImage(0, false);
    });
  }

  function initializeCardTrack(scrollElement, cardSelector, dotSelector, activeCardClass, activeDotClass) {
    if (!scrollElement || scrollElement.dataset.initialized === 'true') return;
    scrollElement.dataset.initialized = 'true';
    const cards = [...scrollElement.querySelectorAll(cardSelector)];
    const scope = scrollElement.parentElement;
    const dots = [...(scope?.querySelectorAll(dotSelector) ?? [])];
    if (!cards.length) return;

    const setActive = (index) => {
      cards.forEach((card, cardIndex) => card.classList.toggle(activeCardClass, cardIndex === index));
      dots.forEach((dot, dotIndex) => dot.classList.toggle(activeDotClass, dotIndex === index));
    };
    const update = () => {
      const center = scrollElement.scrollLeft + scrollElement.clientWidth / 2;
      let active = 0;
      let distance = Infinity;
      cards.forEach((card, index) => {
        const nextDistance = Math.abs(center - (card.offsetLeft + card.offsetWidth / 2));
        if (nextDistance < distance) { distance = nextDistance; active = index; }
      });
      setActive(active);
    };
    scrollElement.addEventListener('scroll', () => window.requestAnimationFrame(update), { passive: true });
    dots.forEach((dot, index) => dot.addEventListener('click', () => {
      const card = cards[index];
      scrollElement.scrollTo({ left: Math.max(0, card.offsetLeft - ((scrollElement.clientWidth - card.offsetWidth) / 2)), behavior: 'smooth' });
      setActive(index);
    }));
    update();
  }

  function initializeProductTracks(root = document) {
    root.querySelectorAll('[data-real-view-scroll]').forEach((track) => initializeCardTrack(track, '[data-real-view-card]', '[data-real-view-dot]', 'real-card--active', 'real-view__dot--active'));
    root.querySelectorAll('[data-peace-scroll]').forEach((track) => initializeCardTrack(track, '[data-peace-card]', '[data-peace-dot]', 'peace-card--active', 'product-peace__dot--active'));
    root.querySelectorAll('[data-installation-scroll]').forEach((track) => initializeCardTrack(track, '[data-installation-card]', '[data-installation-dot]', 'installation-card--active', 'installation__dot--active'));
  }

  function initializeProductFaq(root = document) {
    root.querySelectorAll('.product-faq__item').forEach((item) => {
      if (item.dataset.initialized === 'true') return;
      item.dataset.initialized = 'true';
      const summary = item.querySelector('.product-faq__question');
      const answer = item.querySelector('.product-faq__answer');
      let timer = 0;
      if (!summary || !answer) return;
      item.classList.toggle('product-faq__item--open', item.open);
      summary.addEventListener('click', (event) => {
        event.preventDefault();
        window.clearTimeout(timer);
        if (item.classList.contains('product-faq__item--open')) {
          item.classList.remove('product-faq__item--open');
          timer = window.setTimeout(() => { item.open = false; }, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 380);
          return;
        }
        item.open = true;
        answer.getBoundingClientRect();
        window.requestAnimationFrame(() => item.classList.add('product-faq__item--open'));
      });
    });
  }

  function initializeReviews(root = document) {
    root.querySelectorAll('[data-reviews-scroll]').forEach((track) => {
      if (track.dataset.initialized === 'true') return;
      track.dataset.initialized = 'true';
      const section = track.closest('.reviews');
      const cards = [...track.querySelectorAll('[data-review-card]')];
      const dots = [...(section?.querySelectorAll('[data-review-dot]') ?? [])];
      const previous = section?.querySelector('[data-review-prev]');
      const next = section?.querySelector('[data-review-next]');
      let active = 0;
      if (!cards.length) return;
      const metrics = () => {
        const step = cards[1] ? cards[1].offsetLeft - cards[0].offsetLeft : cards[0].offsetWidth;
        const visible = Math.max(1, Math.floor((track.clientWidth + (step - cards[0].offsetWidth)) / Math.max(1, step)));
        return { step: Math.max(1, step), max: Math.max(0, cards.length - visible) };
      };
      const setActive = (index) => {
        const { max } = metrics();
        active = Math.max(0, Math.min(max, index));
        cards.forEach((card, index) => card.classList.toggle('review-card--active', index === active));
        dots.forEach((dot, index) => { dot.hidden = index > max; dot.classList.toggle('reviews__dot--active', index === active); });
        if (previous) previous.disabled = active === 0;
        if (next) next.disabled = active === max;
      };
      const scrollTo = (index) => { const data = metrics(); setActive(index); track.scrollTo({ left: active * data.step, behavior: 'smooth' }); };
      previous?.addEventListener('click', () => scrollTo(active - 1));
      next?.addEventListener('click', () => scrollTo(active + 1));
      dots.forEach((dot, index) => dot.addEventListener('click', () => scrollTo(index)));
      track.addEventListener('scroll', () => window.requestAnimationFrame(() => setActive(Math.round(track.scrollLeft / metrics().step))), { passive: true });
      const reviewCopies = cards.map((card) => ({
        card,
        quote: card.querySelector('.review-card__quote'),
        button: card.querySelector('[data-review-read-more]')
      }));
      reviewCopies.forEach(({ card, button }) => button?.addEventListener('click', () => {
        const expanded = card.classList.toggle('review-card--expanded');
        button.setAttribute('aria-expanded', String(expanded));
        button.textContent = expanded ? labels.readLess : labels.readMore;
      }));
      const updateLayout = () => {
        const collapsedHeight = 4.8 * parseFloat(window.getComputedStyle(document.documentElement).fontSize);
        reviewCopies.forEach(({ card, quote, button }) => {
          if (!quote || !button) return;
          const truncated = quote.scrollHeight > collapsedHeight + 1;
          card.classList.toggle('review-card--truncated', truncated);
          button.hidden = !truncated;
          if (!truncated) {
            card.classList.remove('review-card--expanded');
            button.setAttribute('aria-expanded', 'false');
            button.textContent = labels.readMore;
          }
        });
        setActive(active);
      };
      updateLayout();
      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(updateLayout);
        observer.observe(track);
        track._reviewsCleanup = () => observer.disconnect();
      }
      document.fonts?.ready.then(() => { if (track.isConnected) updateLayout(); });
    });
  }

  function initializePurchaseHeaderOffset() {
    const header = document.querySelector('[data-site-header]');
    if (!header || !document.querySelector('[data-product-root]') || header.dataset.purchaseOffsetReady === 'true') return;
    header.dataset.purchaseOffsetReady = 'true';
    const update = () => {
      document.documentElement.style.setProperty('--purchase-header-height', `${Math.ceil(header.getBoundingClientRect().height)}px`);
    };
    update();
    if ('ResizeObserver' in window) {
      header._purchaseHeaderObserver = new ResizeObserver(update);
      header._purchaseHeaderObserver.observe(header);
    }
  }

  function initializeStickyPurchase(form, purchaseForm, sticky) {
    const toggle = sticky.querySelector('[data-sticky-toggle]');
    const picker = sticky.querySelector('[data-sticky-picker]');
    const selection = sticky.querySelector('.product-purchase-bar__selection');
    const options = [...sticky.querySelectorAll('[data-sticky-quantity]')];
    const header = document.querySelector('[data-site-header]');
    const controller = new AbortController();
    const events = { signal: controller.signal };
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const motions = new Map();
    const barMotion = { offset: '0 calc(100% + 1px)', scale: '1', enter: 280, exit: 190 };
    const pickerMotion = { offset: '0 12px', scale: '0.97', enter: 210, exit: 140 };
    let frame = 0;
    let pointerInteraction = false;

    const syncBarHeight = () => {
      const height = sticky.hidden ? 0 : Math.ceil(sticky.getBoundingClientRect().height);
      if (document.documentElement.style.getPropertyValue('--product-purchase-bar-height') !== `${height}px`) {
        document.documentElement.style.setProperty('--product-purchase-bar-height', `${height}px`);
      }
    };
    const animateVisibility = (element, open, motion) => {
      if (!element) return;
      const previous = motions.get(element);
      element.inert = !open;
      element.setAttribute('aria-hidden', String(!open));
      if (previous ? previous.open === open : element.hidden === !open) return;

      // Continue from the current frame when scrolling or toggling reverses an animation.
      const computed = previous?.animation ? window.getComputedStyle(element) : null;
      const current = computed ? { opacity: computed.opacity, translate: computed.translate, scale: computed.scale } : null;
      previous?.animation?.cancel();
      const state = { open, animation: null };
      motions.set(element, state);
      if (typeof element.animate !== 'function') {
        element.hidden = !open;
        if (element === sticky) syncBarHeight();
        return;
      }

      element.hidden = false;
      const shown = reducedMotion.matches ? { opacity: 1 } : { opacity: 1, translate: '0 0', scale: '1' };
      const concealed = reducedMotion.matches ? { opacity: 0 } : { opacity: 0, translate: motion.offset, scale: motion.scale };
      const start = current ? (reducedMotion.matches ? { opacity: current.opacity } : current) : (open ? concealed : shown);
      state.animation = element.animate([start, open ? shown : concealed], {
        duration: reducedMotion.matches ? 80 : (open ? motion.enter : motion.exit),
        easing: open ? 'cubic-bezier(0.16, 1, 0.3, 1)' : 'cubic-bezier(0.4, 0, 1, 1)',
        fill: 'both'
      });
      state.animation.onfinish = () => {
        if (motions.get(element) !== state) return;
        element.hidden = !state.open;
        state.animation.cancel();
        state.animation = null;
        if (element === sticky) syncBarHeight();
      };
    };
    const setPickerOpen = (open, restoreFocus = false) => {
      if (!toggle || !picker) return;
      animateVisibility(picker, open, pickerMotion);
      toggle.setAttribute('aria-expanded', String(open));
      // Keep keyboard focus management from interrupting a mobile tap.
      if (open && !pointerInteraction) (options.find((option) => option.getAttribute('aria-pressed') === 'true') || options[0])?.focus({ preventScroll: true });
      else if (restoreFocus && !pointerInteraction && !sticky.hidden && !sticky.inert) toggle.focus({ preventScroll: true });
    };
    const updateVisibility = () => {
      frame = 0;
      const headerBottom = Math.max(0, header?.getBoundingClientRect().bottom || 0);
      const pastForm = form.getBoundingClientRect().bottom <= headerBottom;
      if (!pastForm) {
        if (sticky.contains(document.activeElement)) form.querySelector('input[data-quantity]:checked')?.focus({ preventScroll: true });
        if (picker && !picker.hidden) setPickerOpen(false);
      }
      animateVisibility(sticky, pastForm, barMotion);
      syncBarHeight();
    };
    const scheduleVisibility = () => {
      if (!frame) frame = window.requestAnimationFrame(updateVisibility);
    };

    toggle?.addEventListener('click', () => {
      if (purchaseForm.dataset.submitting !== 'true' && !sticky.hidden && !sticky.inert) setPickerOpen(toggle.getAttribute('aria-expanded') !== 'true');
    }, events);
    picker?.querySelector('[data-sticky-picker-close]')?.addEventListener('click', () => setPickerOpen(false, true), events);
    const selectOption = (option) => {
      if (purchaseForm.dataset.submitting === 'true' || toggle?.getAttribute('aria-expanded') !== 'true' || sticky.inert) return;
      const input = form.querySelector(`input[data-quantity="${option.dataset.stickyQuantity}"]`);
      if (!input || input.disabled) return;
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      setPickerOpen(false, true);
    };
    options.forEach((option) => {
      let tap = null;
      option.addEventListener('pointerdown', (event) => {
        tap = event.isPrimary && event.pointerType !== 'mouse' && event.button === 0
          ? { id: event.pointerId, x: event.clientX, y: event.clientY }
          : null;
      }, events);
      option.addEventListener('pointermove', (event) => {
        if (tap && Math.hypot(event.clientX - tap.x, event.clientY - tap.y) > 10) tap = null;
      }, events);
      option.addEventListener('pointercancel', () => { tap = null; }, events);
      option.addEventListener('pointerup', (event) => {
        const start = tap;
        tap = null;
        if (!start || event.pointerId !== start.id || Math.hypot(event.clientX - start.x, event.clientY - start.y) > 10) return;
        // Commit a tap before mobile focus changes or a delayed compatibility click.
        event.preventDefault();
        selectOption(option);
      }, events);
      // Mouse, keyboard and assistive activation retain the native click path.
      // A click following pointerup is ignored once selectOption closes the picker.
      option.addEventListener('click', () => selectOption(option), events);
    });
    document.addEventListener('pointerdown', (event) => {
      pointerInteraction = true;
      if (!selection?.contains(event.target)) setPickerOpen(false);
    }, events);
    document.addEventListener('keydown', () => { pointerInteraction = false; }, { ...events, capture: true });
    selection?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && toggle?.getAttribute('aria-expanded') === 'true') {
        event.preventDefault();
        event.stopPropagation();
        setPickerOpen(false, true);
      }
    }, events);
    document.addEventListener('focusin', (event) => {
      // Outside taps are handled by pointerdown; focus dismissal is for keyboard navigation.
      if (!pointerInteraction && !selection?.contains(event.target)) setPickerOpen(false);
    }, events);
    purchaseForm.addEventListener('submit', () => setPickerOpen(false), events);
    window.addEventListener('scroll', scheduleVisibility, { ...events, passive: true });
    window.addEventListener('resize', scheduleVisibility, events);
    window.addEventListener('pageshow', scheduleVisibility, events);
    reducedMotion.addEventListener?.('change', () => {
      if (reducedMotion.matches) motions.forEach((state) => state.animation?.finish());
    }, events);
    if ('ResizeObserver' in window) {
      form._purchaseBarObserver = new ResizeObserver(scheduleVisibility);
      form._purchaseBarObserver.observe(sticky);
      form._purchaseBarObserver.observe(form);
      if (header) form._purchaseBarObserver.observe(header);
    }
    form._purchaseBarCleanup = () => {
      controller.abort();
      form._purchaseBarObserver?.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
      motions.forEach((state) => state.animation?.cancel());
      motions.clear();
      if (picker) {
        picker.hidden = true;
        picker.inert = true;
        picker.setAttribute('aria-hidden', 'true');
      }
      toggle?.setAttribute('aria-expanded', 'false');
      sticky.hidden = true;
      sticky.inert = true;
      sticky.setAttribute('aria-hidden', 'true');
    };
    updateVisibility();
  }

  function initializePurchaseConfirmation(purchaseForm) {
    const input = purchaseForm?.querySelector('[data-compatibility-confirmation]');
    const confirmation = input?.closest('[data-purchase-confirmation]');
    const error = confirmation?.querySelector('[data-compatibility-error]');
    const status = confirmation?.querySelector('[data-compatibility-status]');
    if (!input || !confirmation || !error) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let shakeAnimation;
    const stopShake = () => {
      shakeAnimation?.cancel();
      shakeAnimation = null;
    };
    const shakeConfirmation = () => {
      stopShake();
      if (reducedMotion.matches || typeof confirmation.animate !== 'function') return;
      shakeAnimation = confirmation.animate([
        { transform: 'translateX(0)', offset: 0 },
        { transform: 'translateX(-4px)', offset: 0.12 },
        { transform: 'translateX(4px)', offset: 0.25 },
        { transform: 'translateX(-4px)', offset: 0.38 },
        { transform: 'translateX(4px)', offset: 0.51 },
        { transform: 'translateX(-2px)', offset: 0.64 },
        { transform: 'translateX(2px)', offset: 0.78 },
        { transform: 'translateX(0)', offset: 1 }
      ], { duration: 1000, easing: 'ease-in-out' });
    };
    reducedMotion.addEventListener?.('change', () => {
      if (reducedMotion.matches) stopShake();
    });

    const clearError = () => {
      stopShake();
      confirmation.classList.remove('is-invalid');
      input.removeAttribute('aria-invalid');
      input.removeAttribute('aria-describedby');
      error.hidden = true;
    };

    const syncCompatibility = () => {
      const verified = Boolean(compatibilityResult);
      input.checked = verified;
      if (status) status.hidden = !verified;
      confirmation.classList.toggle('is-verified', verified);
      clearError();
    };
    document.addEventListener('kiddyview:compatibility-change', syncCompatibility);
    purchaseForm._compatibilityCleanup = () => document.removeEventListener('kiddyview:compatibility-change', syncCompatibility);
    syncCompatibility();

    input.addEventListener('invalid', (event) => {
      event.preventDefault();
      confirmation.classList.add('is-invalid');
      error.hidden = false;
      input.setAttribute('aria-invalid', 'true');
      input.setAttribute('aria-describedby', error.id);
      input.focus({ preventScroll: true });
      confirmation.scrollIntoView({ block: 'center', behavior: 'instant' });
      shakeConfirmation();
    });
    input.addEventListener('change', () => {
      if (input.checked) clearError();
      else if (compatibilityResult) setCompatibilityResult(false);
    });
    purchaseForm.addEventListener('reset', () => {
      clearError();
      // Native reset runs after this event; reapply a completed check afterwards.
      window.setTimeout(syncCompatibility, 0);
    });
  }

  function initializeProductForms(root = document) {
    root.querySelectorAll('.product-form').forEach((form) => {
      if (form.dataset.initialized === 'true') return;
      form.dataset.initialized = 'true';
      const purchaseForm = form.querySelector('[data-product-form]');
      initializePurchaseConfirmation(purchaseForm);
      const sticky = document.getElementById(purchaseForm?.dataset.stickyId || '');
      const submits = purchaseForm ? [...purchaseForm.elements].filter((control) => control.matches('[data-product-submit]')) : [];
      const quantityInput = form.querySelector('[data-product-quantity-input]');
      const defaultBundle = form.querySelector('input[data-quantity="1"]');
      if (defaultBundle) defaultBundle.checked = true;
      const updateBundle = () => {
        const selected = form.querySelector('input[data-quantity]:checked');
        if (!selected) return;
        const quantity = Math.max(1, Number(selected.dataset.quantity) || 1);
        const total = globalThis.KiddyViewProductLogic.bundleTotal(
          Number(form.dataset.unitPrice),
          Number(form.dataset.pairDiscount),
          quantity
        );
        if (quantityInput) quantityInput.value = String(quantity);
        const formattedTotal = globalThis.KiddyViewProductLogic.formatMoney(total, form.dataset.currency, document.documentElement.lang);
        const available = form.dataset.productAvailable === 'true';
        submits.forEach((submit) => {
          const cartLabel = submit.querySelector('[data-cart-label]');
          if (cartLabel && labels.addToCart) {
            cartLabel.textContent = !available && form.dataset.unavailableLabel
              ? form.dataset.unavailableLabel
              : cartLabel.hasAttribute('data-cart-label-short') ? labels.addToCart : `${labels.addToCart} (${formattedTotal})`;
          }
          submit.disabled = !available || purchaseForm.dataset.submitting === 'true';
        });
        if (sticky) {
          sticky.querySelector('[data-sticky-bundle]').textContent = selected.dataset.bundleLabel || '';
          sticky.querySelector('[data-sticky-total]').textContent = formattedTotal;
          const compareTotal = Math.max(Number(form.dataset.unitPrice) || 0, Number(form.dataset.unitComparePrice) || 0) * quantity;
          const compare = sticky.querySelector('[data-sticky-compare]');
          if (compare) {
            compare.hidden = compareTotal <= total;
            compare.textContent = compareTotal > total ? globalThis.KiddyViewProductLogic.formatMoney(compareTotal, form.dataset.currency, document.documentElement.lang) : '';
          }
          sticky.classList.toggle('is-on-sale', compareTotal > total);
          sticky.querySelectorAll('[data-sticky-quantity]').forEach((option) => option.setAttribute('aria-pressed', String(Number(option.dataset.stickyQuantity) === quantity)));
        }
      };
      form.querySelectorAll('input[data-quantity]').forEach((input) => input.addEventListener('change', updateBundle));
      updateBundle();
      if (sticky && purchaseForm) initializeStickyPurchase(form, purchaseForm, sticky);
    });
  }

  function initializeWithdrawal(root = document) {
    root.querySelectorAll('[data-withdrawal-form] form').forEach((form) => {
      if (form.dataset.initialized === 'true') return;
      form.dataset.initialized = 'true';
      const one = form.querySelector('[data-withdrawal-step="1"]');
      const two = form.querySelector('[data-withdrawal-step="2"]');
      const next = form.querySelector('[data-withdrawal-next]');
      const back = form.querySelector('[data-withdrawal-back]');
      const summary = form.querySelector('[data-withdrawal-summary]');
      const status = form.querySelector('[data-withdrawal-status]');
      const bodyInput = form.querySelector('[data-withdrawal-body]');
      const indicators = [...document.querySelectorAll('[data-step-indicator]')];
      const values = () => { const data = new FormData(form); return { customerName: String(data.get('contact[name]') || '').trim(), orderNumber: String(data.get('contact[Ordernummer]') || '').trim(), email: String(data.get('contact[email]') || '').trim(), scope: String(data.get('contact[Omvang]') || ''), products: String(data.get('contact[Producten]') || '').trim() }; };
      const showStep = (number) => { if (one) one.hidden = number !== 1; if (two) two.hidden = number !== 2; indicators.forEach((item) => item.classList.toggle('withdrawal-steps__item--active', item.dataset.stepIndicator === String(number))); document.querySelector('#formulier')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); };
      next?.addEventListener('click', () => {
        const valid = [...(one?.querySelectorAll('[required]') ?? [])].every((field) => field.checkValidity());
        if (!valid) { form.reportValidity(); return; }
        const data = values();
        if (summary) {
          summary.replaceChildren();
          [[labels.name, data.customerName], [labels.orderNumber, data.orderNumber], [labels.confirmationTo, data.email], [labels.scope, data.scope], [labels.products, data.products || labels.notSpecified]].forEach(([term, value]) => {
            const wrapper = document.createElement('div');
            const title = document.createElement('dt');
            const description = document.createElement('dd');
            title.textContent = term;
            description.textContent = value;
            wrapper.append(title, description);
            summary.append(wrapper);
          });
        }
        if (status) status.hidden = true;
        showStep(2);
      });
      back?.addEventListener('click', () => showStep(1));
      form.addEventListener('submit', (event) => {
        if (!two || two.hidden) {
          event.preventDefault();
          next?.click();
          return;
        }
        const data = values();
        const body = `${labels.name}: ${data.customerName}\n${labels.orderNumber}: ${data.orderNumber}\n${labels.email}: ${data.email}\n${labels.scope}: ${data.scope}\n${labels.products}: ${data.products || labels.notSpecified}\n\n${labels.withdrawalStatement}`;
        if (bodyInput) bodyInput.value = body;
      });
    });
  }

  function initializeVideoModal(root = document) {
    const modal = document.querySelector('[data-modal]');
    if (!modal || modal.dataset.initialized === 'true') return;
    modal.dataset.initialized = 'true';
    const toggle = (open) => {
      modal.setAttribute('aria-hidden', String(!open));
      document.body.classList.toggle('modal-open', open);
    };
    document.querySelectorAll('[data-modal-open]').forEach((button) => button.addEventListener('click', () => toggle(true)));
    modal.querySelectorAll('[data-modal-close]').forEach((button) => button.addEventListener('click', () => toggle(false)));
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape') toggle(false); });
  }

  function initializeLiveView(root = document) {
    root.querySelectorAll('[data-live-screen]').forEach((screen) => {
      if (screen.dataset.initialized === 'true') return;
      screen.dataset.initialized = 'true';
      const section = screen.closest('.live-view');
      const buttons = [...(section?.querySelectorAll('[data-live-mode]') ?? [])];
      buttons.forEach((button) => button.addEventListener('click', () => {
        const mode = button.dataset.liveMode;
        buttons.forEach((item) => {
          const active = item === button;
          item.classList.toggle('live-view__toggle-btn--active', active);
          item.setAttribute('aria-selected', String(active));
        });
        screen.dataset.mode = mode;
        screen.querySelectorAll('[data-live-image]').forEach((image) => image.setAttribute('aria-hidden', String(image.dataset.liveImage !== mode)));
      }));
    });
  }

  function initializeHomepageTracks(root = document) {
    root.querySelectorAll('[data-steps-scroll]').forEach((track) => initializeCardTrack(track, '[data-step-card]', '[data-step-dot]', 'step-card--active', 'how-it-works__dot--active'));
    root.querySelectorAll('[data-comfort-scroll]').forEach((track) => initializeCardTrack(track, '[data-comfort-card]', '[data-comfort-dot]', 'comfort-card--active', 'comfort__dot--active'));
  }

  function initialize(root = document) {
    initializeVideoModal(root);
    initializeLiveView(root);
    initializeHomepageTracks(root);
    initializeFaq(root);
    initializeProcess(root);
    initializeProductGallery(root);
    initializeProductTracks(root);
    initializeProductFaq(root);
    initializeReviews(root);
    initializeProductForms(root);
    initializePurchaseHeaderOffset();
    initializeWithdrawal(root);
  }

  initialize();
  window.addEventListener('hashchange', openLinkedFaq);
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    document.querySelectorAll('[data-product-root] input[data-quantity="1"]').forEach((input) => {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('.product-form').forEach((form) => {
      form._purchaseBarCleanup?.();
      form.querySelector('[data-product-form]')?._compatibilityCleanup?.();
    });
    event.target.querySelectorAll('[data-reviews-scroll]').forEach((track) => track._reviewsCleanup?.());
    event.target.querySelectorAll('[data-site-header]').forEach((header) => {
      header._purchaseHeaderObserver?.disconnect();
      document.documentElement.style.removeProperty('--purchase-header-height');
    });
    if (event.target.querySelector('[data-product-sticky]')) document.documentElement.style.removeProperty('--product-purchase-bar-height');
  });
})();
